import React, { useCallback, useMemo } from 'react';

import { Button, Grid, LinearProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import { useIsMobile } from '../../../hooks/useBreakpoints';
import { IconArrowLeft } from '../../icon';

type PageControlProps = {
  currentPage: number;
  totalPages: number;
  nextPage: () => void;
  prevPage: () => void;
  onCancel?: () => void;
  saveDraft?: () => void;
  canSubmit: boolean;
  hideNextBtn?: boolean;
  isSubmitting?: boolean;
  setToastMsg: React.Dispatch<React.SetStateAction<string | null>>;
  setErrorsVisible: React.Dispatch<React.SetStateAction<boolean>>;
};

const useStyles = makeStyles(() => ({
  leftBtnWrapper: (isMobile) => ({
    marginTop: isMobile ? '0px' : '12px',
  }),
}));

const PageControl: React.FC<PageControlProps> = ({
  currentPage,
  totalPages,
  nextPage,
  prevPage,
  saveDraft,
  canSubmit,
  onCancel,
  setToastMsg,
  hideNextBtn = false,
  isSubmitting,
  setErrorsVisible,
}) => {
  const isMobile = useIsMobile();
  const classes = useStyles(isMobile);
  const { isLastPage, pctComplete } = useMemo(
    () => ({
      isLastPage: currentPage === totalPages,
      pctComplete: Math.round((currentPage / totalPages) * 100),
    }),
    [currentPage, totalPages],
  );

  const onNextPage = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      nextPage();
    },
    [nextPage],
  );

  const onSubmitError = useCallback(
    (e: React.FormEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setToastMsg('Please fix all missing/incorrect form fields');
      setErrorsVisible(true);
    },
    [setToastMsg, setErrorsVisible],
  );

  return (
    <Grid
      container
      justifyContent="center"
      {...(isMobile && { alignItems: 'flex-end' })}
    >
      <Grid item xs={6}>
        {!isMobile && (
          <Grid style={{ maxWidth: '184px', width: '100%' }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                {pctComplete}% complete
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <LinearProgress variant="determinate" value={pctComplete} />
            </Grid>
          </Grid>
        )}
        <Grid item className={classes.leftBtnWrapper}>
          {currentPage === 1 && onCancel ? (
            <Button
              onClick={onCancel}
              variant="outlined"
              disableElevation={true}
              color="inherit"
              style={{ backgroundColor: 'white' }}
            >
              Cancel
            </Button>
          ) : (
            <Button
              disabled={currentPage <= 1}
              variant="text"
              startIcon={<IconArrowLeft />}
              onClick={prevPage}
            >
              Back
            </Button>
          )}
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Grid container justifyContent="flex-end" spacing={2}>
          {isLastPage ? (
            <>
              {saveDraft && (
                <Grid item>
                  <Button
                    onClick={saveDraft}
                    variant="outlined"
                    disableElevation={true}
                    color="inherit"
                    style={{ backgroundColor: 'white' }}
                  >
                    Save Draft
                  </Button>
                </Grid>
              )}
              <Grid item>
                <Button
                  type={canSubmit ? 'submit' : 'button'}
                  variant="contained"
                  disableElevation={true}
                  color="primary"
                  disabled={canSubmit && isSubmitting}
                  {...(!canSubmit && { onClick: onSubmitError })}
                >
                  Submit
                </Button>
              </Grid>
            </>
          ) : (
            <Grid item>
              {!hideNextBtn && (
                <Button
                  variant="contained"
                  disableElevation={true}
                  color="primary"
                  onClick={onNextPage}
                >
                  Next Step
                </Button>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PageControl;
